import type { BoardStage } from 'chery-contracts';

/**
 * Page 02 — the operating model: the organisation chart, the accountability matrix, the journey stages and one card
 * per division (mission, KPIs, responsibilities, reporting, agenda, deliverables), as the board printed them. Static
 * copy (decision 3); the report sheets under each stage are live (`board.stageSheets`).
 */
export const OPERATING_MODEL_INTRO = {
  eyebrow: 'From the Board · Page 02',
  title: 'The operating model — who owns what',
  body: 'The structure first, then the journey. Select a stage of the journey to open its divisions — mandate, deliverables to the board, and the print-ready reports.',
} as const;

// ---------------------------------------------------------------------------------------------------------------
// Organisation chart

export interface OrgPerson {
  role: string;
  /** `— vacant —` where the board shows an open seat. */
  name: string;
  /** In-house (solid ink) or outsourced (accent) resource. */
  outsourced?: boolean;
  reports?: readonly OrgPerson[];
}

export const ORG_CHART_CAPTION = 'Organization structure — team plan';
export const ORG_CHART_TITLE = 'The team behind the machine';
export const VACANT = '— vacant —';

export const ORG_CEO: OrgPerson = { role: 'CEO', name: 'Mamdouh Younis' };

/** The six branches under the CEO, left to right. */
export const ORG_BRANCHES: readonly OrgPerson[] = [
  { role: 'PP manager', name: 'Laith Abu Hammad', reports: [{ role: 'PP officer', name: 'Yousef Hafez' }] },
  {
    role: 'LOG manager',
    name: 'Mohammed Abu Khalifeh',
    outsourced: true,
    reports: [
      { role: 'Storage', name: 'Jehan', outsourced: true },
      { role: 'Homologation', name: 'Rama', outsourced: true },
      { role: 'Shipping', name: 'Raghad', outsourced: true },
    ],
  },
  { role: 'MRK director', name: 'Jose Manuel', outsourced: true, reports: [{ role: 'MRK manager', name: VACANT, outsourced: true }] },
  {
    role: 'S&M manager',
    name: 'Mohammed Sheqwara',
    reports: [
      {
        role: 'RET + USE supervisor',
        name: VACANT,
        reports: [
          { role: 'SAL senior officer', name: 'Saed M' },
          { role: 'SAL senior officer', name: 'Yasmin M' },
          { role: 'SAL officer', name: 'Sarah J' },
        ],
      },
      { role: 'CORP + TEND supervisor', name: VACANT, reports: [{ role: 'SAL officer', name: VACANT }] },
      { role: 'NET supervisor', name: 'A Rousan' },
      { role: 'SAL support officer', name: VACANT },
    ],
  },
  {
    role: 'AFS director',
    name: 'David Lawlor',
    outsourced: true,
    reports: [
      {
        role: 'SER manager',
        name: VACANT,
        outsourced: true,
        reports: [
          { role: 'Team support', name: 'Sami', outsourced: true },
          { role: 'Technical', name: 'Adel Hadjiri', outsourced: true },
          { role: 'CRM + complaints', name: 'Bisher', outsourced: true },
          { role: 'Warranty', name: 'Rowzan', outsourced: true },
        ],
      },
      { role: 'BOD manager', name: 'Samer A', outsourced: true },
      {
        role: 'SP manager',
        name: VACANT,
        outsourced: true,
        reports: [
          { role: 'LOG + stock', name: 'Fadi Muafi', outsourced: true },
          { role: 'Stock management', name: 'Zaid', outsourced: true },
          { role: 'Service center', name: VACANT, outsourced: true },
          { role: 'WH', name: VACANT, outsourced: true },
        ],
      },
    ],
  },
  {
    role: 'CFO',
    name: 'Mohammed Abu Dabaseh',
    reports: [{ role: 'F&A manager', name: 'Fadi Ghawi', reports: [{ role: 'Accountant', name: VACANT }] }],
  },
];

export const SUPPORT_FUNCTIONS: readonly string[] = ['HR', 'IT', 'Procurement', 'Business services', 'BE', 'DX', 'CR', 'CX'];

// ---------------------------------------------------------------------------------------------------------------
// Journey stages and the accountability matrix

export interface StageCopy {
  stage: BoardStage;
  /** URL slug (`/board/operating-model?stage=selling`). */
  slug: string;
  name: string;
  /** The division ids (keys of `DIVISION_CARDS`) of the stage. */
  divisionIds: readonly string[];
  reportTitle: string;
}

export const STAGES: readonly StageCopy[] = [
  { stage: 'GETTING', slug: 'getting', name: '1 · Getting the vehicles', divisionIds: ['pp', 'log'], reportTitle: 'Getting the vehicles (PP + LOG)' },
  {
    stage: 'SELLING',
    slug: 'selling',
    name: '2 · Selling the vehicles',
    divisionIds: ['mrk', 'sal', 'cx'],
    reportTitle: 'Selling the vehicles (MRK + SAL + CR + CX)',
  },
  { stage: 'SERVICING', slug: 'servicing', name: '3 · Servicing', divisionIds: ['ser'], reportTitle: 'Servicing the vehicles (SER/BP)' },
  { stage: 'PARTS', slug: 'parts', name: '4 · Parts', divisionIds: ['sp'], reportTitle: 'Spare parts (SP)' },
  { stage: 'FINANCE', slug: 'finance', name: '5 · Finance & accounting', divisionIds: ['fa'], reportTitle: 'Finance & accounting (FIN + ACC)' },
];

export const MATRIX_CAPTION = 'Fig. 3 — Accountability matrix';
export const MATRIX_TITLE = 'Which division moves which board number';
export const MATRIX_HINT = 'Select any division to open its stage — mandate, deliverables and reports.';
export const MATRIX_COLUMNS = ['Revenue', 'Volume', 'Share', 'P&L', 'Cash'] as const;

/** 2 = direct (owns the number), 1 = indirect (enables or carries cost), 0 = none. */
export type MatrixLevel = 0 | 1 | 2;

export interface MatrixRow {
  code: string;
  who: string;
  stage: BoardStage;
  stageLabel: string;
  cells: readonly [MatrixLevel, MatrixLevel, MatrixLevel, MatrixLevel, MatrixLevel];
}

export const MATRIX: readonly MatrixRow[] = [
  { code: 'PP', who: 'Product Planning', stage: 'GETTING', stageLabel: '1 · Getting', cells: [0, 2, 2, 2, 0] },
  { code: 'LOG', who: 'Logistics', stage: 'GETTING', stageLabel: '1 · Getting', cells: [0, 1, 0, 1, 0] },
  { code: 'MRK', who: 'Marketing', stage: 'SELLING', stageLabel: '2 · Selling', cells: [0, 2, 2, 1, 0] },
  { code: 'SAL', who: 'Sales', stage: 'SELLING', stageLabel: '2 · Selling', cells: [2, 2, 0, 0, 2] },
  { code: 'CR/CX', who: 'Customer Relations', stage: 'SELLING', stageLabel: '2 · Selling', cells: [0, 1, 1, 0, 0] },
  { code: 'SER/BP', who: 'Service & Body', stage: 'SERVICING', stageLabel: '3 · Servicing', cells: [2, 1, 0, 0, 2] },
  { code: 'SP', who: 'Spare Parts', stage: 'PARTS', stageLabel: '4 · Parts', cells: [2, 1, 0, 0, 2] },
  { code: 'FA', who: 'Finance', stage: 'FINANCE', stageLabel: '5 · Finance', cells: [0, 0, 0, 2, 2] },
];

export const MATRIX_LEGEND = { direct: 'Direct — owns the number', indirect: 'Indirect — enables or carries cost' } as const;

export const MATRIX_NOTE =
  "Every division touches at least two board numbers; only Sales touches volume, revenue and cash at once — which is why the per-vehicle sales plan is the board's most-watched document.";

// ---------------------------------------------------------------------------------------------------------------
// Division cards

export interface Deliverable {
  tag: string;
  what: string;
}

export interface DivisionCard {
  id: string;
  code: string;
  name: string;
  stage: string;
  lead: string;
  model: string;
  mission: string;
  kpis: readonly string[];
  responsibilities: readonly string[];
  reporting: readonly string[];
  agenda: readonly string[];
  deliverables: readonly Deliverable[];
}

export const DIVISION_CARDS: Readonly<Record<string, DivisionCard>> = {
  pp: {
    id: 'pp',
    code: 'PP',
    name: 'Product Planning',
    stage: 'Stage 1 — Getting the vehicles',
    lead: 'Laith Abu Hammad',
    model: 'Reports to acting CEO · gatekeeper of the catalog',
    mission:
      'Owns what we sell and at what price. Reads the market, plans the orders, manages the supplier relationship, and gatekeeps everything that enters the catalog — vehicles, services, value-add packages and, later, accessories.',
    kpis: ['Volume — direct', 'Market share — direct', 'P&L — sets pricing'],
    responsibilities: [
      'Competitive & market analysis: competitor lists, market health reports',
      'Ordering process: annual vehicle order plan, products & specs list, product packages (financing, pricing, value-add)',
      'Vehicle ordering: multi-supplier sourcing, order management, correct specifications',
      'Official price lists — clear, traceable, dated',
      'Supplier relationship with Chery and other partners',
    ],
    reporting: ['Order funnel: yearly target → monthly plan → orders placed', 'Vehicles en route to Jordan', 'Vehicles in storage, with status'],
    agenda: [
      'EQ7 PDI plan',
      '2026 month-by-month orders',
      '’23–’26 stock review per car',
      'Product price list & products list with marketing materials',
      'Storage space & care plans',
    ],
    deliverables: [
      { tag: 'Order plan', what: '2026 month-by-month vehicle order plan — the source of every downstream volume number' },
      { tag: 'Price list', what: 'Official dated price list + products & specs list, packaged with marketing materials' },
      { tag: 'Stock review', what: '’23–’26 stock review per car — aging units priced to move before the relaunch' },
      { tag: 'PDI plan', what: 'EQ7 pre-delivery inspection plan with storage space & care coverage' },
    ],
  },
  log: {
    id: 'log',
    code: 'LOG',
    name: 'Logistics',
    stage: 'Stage 1 — Getting the vehicles',
    lead: 'Mohammed Abu Khalifeh',
    model: 'Logistics manager under Product Planning',
    mission:
      'Makes sure every vehicle Product Planning orders arrives, clears, and is stored in sellable condition — availability is what lets Sales hit volume.',
    kpis: ['Volume — enabler', 'P&L — cost carried'],
    responsibilities: [
      'Vehicle transfer: transport options, in-transit tracking, cargo insurance',
      'Storage: locations, capacities, environment; vehicle care plan (charging, movement)',
      'Government procedures: approvals, certifications, customs clearances',
      'Pre-delivery inspection (PDI) execution',
    ],
    reporting: ['Vehicles requested / in transport', 'Vehicles en route', 'Stored vehicles and their condition'],
    agenda: ['Shipping options list (deal or no deal)', 'Storage space plan', 'PDI process checklist'],
    deliverables: [
      { tag: 'Shipping', what: 'Shipping options list, deal-or-no-deal — cost per unit decides the landed margin' },
      { tag: 'Storage', what: 'Storage space plan with environment & care schedule (EVs need charging rotation)' },
      { tag: 'PDI', what: 'PDI process checklist signed off before the first relaunch delivery' },
    ],
  },
  mrk: {
    id: 'mrk',
    code: 'MRK',
    name: 'Marketing',
    stage: 'Stage 2 — Selling the vehicles',
    lead: 'Jose Manuel',
    model: 'Marketing director · reports to acting CEO',
    mission:
      'Two jobs: build brand equity for a brand Jordan doesn’t yet know, and feed Sales a steady stream of qualified leads — an always-on sales campaign across the full 6-month customer journey.',
    kpis: ['Volume — direct', 'Market share — direct', 'P&L — cost side'],
    responsibilities: [
      'Customer journey, touchpoints and messaging guidelines — localized on "In every home, for every family"',
      'Marketing plan & 500K JOD budget: digital 45%, launches 20%, outdoor 15%, PR, CRM, test drives',
      'Lead funnel management: capture → qualify → hand to Sales',
      'Personas & community: comfort seekers, value-for-money, women, eco/tech buyers',
      'Launch readiness built ahead for every 2027 model',
    ],
    reporting: [
      'Activity calendar: brand campaigns, model-by-model campaigns',
      'Leads generated and qualified leads',
      'Brand awareness & consideration levels, CPL, conversion, ROI',
    ],
    agenda: ['Marketing plan agreement — full activities, # leads needed', 'Personas building'],
    deliverables: [
      { tag: 'Plan', what: 'Marketing plan agreement — full activity calendar and the # of leads Sales needs to hit 100/month' },
      { tag: 'Personas', what: 'Persona set: comfort seekers, value-for-money, women, eco/tech buyers — one message each' },
      {
        tag: 'Funnel',
        what: 'Lead funnel sized backwards from sales: a 6-month, ~200-interaction journey means the funnel must fill two quarters ahead of each target month',
      },
    ],
  },
  sal: {
    id: 'sal',
    code: 'SAL',
    name: 'Sales',
    stage: 'Stage 2 — Selling the vehicles',
    lead: 'Mohammed Sheqwara',
    model: 'Acting sales & marketing manager · showroom team of three',
    mission:
      'Sells every allocated unit through three channels — and can name, vehicle by vehicle, who it expects to sell each one to: 568 in 2026, 1,473 in 2027 and 2,407 in 2028 — per the one-sheet plan.',
    kpis: ['Volume — direct', 'Revenue — direct', 'Cash — collections'],
    responsibilities: [
      'Per-vehicle sales plan: channel, salesman and target customer for each unit',
      'Fleet: named target-company list; Retail: waiting lists, referrals, target profiles',
      'Weekly activity volume: leads handled, calls, visits, showroom traffic, test drives, offers, closings, deliveries',
      'Channel supervision: retail, corporate/fleet, traders & dealers network',
    ],
    reporting: ['Deals closed and vehicles delivered', 'Activity per client, worked back from marketing leads', 'Pipeline vs the per-vehicle plan'],
    agenda: ['2026 sales plan — target clients per channel, per VIN (28/07)', 'Sales process review (27/07)', 'Network sales plan'],
    deliverables: [
      { tag: 'Per-VIN plan', what: '2026 sales plan naming a target client, channel and salesman for every VIN — no anonymous stock' },
      { tag: 'Process', what: 'Sales process review — lead to delivery, with weekly activity counts (calls, visits, test drives, offers)' },
      { tag: 'Network', what: 'Traders & dealers network sales plan — the third channel beyond retail and fleet' },
    ],
  },
  cx: {
    id: 'cx',
    code: 'CR/CX',
    name: 'Customer Relations & Experience',
    stage: 'Stage 2 — Selling the vehicles',
    lead: 'Group call center',
    model: 'Group Innovation Center & Business Excellence',
    mission:
      'Owns the customer’s experience around the sale: the call center, complaint resolution and CRM follow-through that turn a relaunch into rebuilt trust.',
    kpis: ['Volume — conversion support', 'Market share — reputation'],
    responsibilities: [
      'Call center: inquiries, lead routing, appointment bookings, follow-ups',
      'Full customer complaints review — every vehicle with an issue',
      'CRM coordination across all touchpoints',
      'Omnichannel continuity: showroom → WhatsApp → email → purchase',
    ],
    reporting: ['Complaint status across all affected vehicles', 'Response times and follow-up completion'],
    agenda: ['Full customer complaints review — all vehicles with issues'],
    deliverables: [
      { tag: 'Complaints', what: 'Full complaints review across every affected vehicle — the trust rebuild starts with the open cases' },
      { tag: 'Routing', what: 'Call-center scripts and lead routing: inquiry → qualified lead → booked appointment' },
    ],
  },
  ser: {
    id: 'ser',
    code: 'SER/BP',
    name: 'Service Centers & Body Shop',
    stage: 'Stage 3 — Servicing the vehicles',
    lead: 'SER Manager — vacant · BOD: Samer A',
    model: 'Reports to Aftersales Director · dotted to acting sales & marketing manager',
    mission:
      'Keeps every sold car on the road under one roof: proactive quick maintenance, reactive repairs, and accident & paint work — with a booking process that gets customers in on time and aftersales revenue at a profit.',
    kpis: ['Revenue — direct', 'Cash — collections', 'Volume — retention'],
    responsibilities: [
      'Four work types: quick lube (oils, fluids, brakes, filters), reactive mechanical/electrical faults, accident repair, paint & cosmetic work',
      'Booking process & proactive customer contact for on-time service',
      'Capacity plan: bays, locations, mobile service units',
      'Service & repair menu with flat rates and times; agreed labor + parts pricing',
      'Warranty process, handling and monitoring; field technical unit',
      'Value-add pipeline: polishing, nano-ceramic, graphene',
    ],
    reporting: [
      'Weekly transactions: vehicles serviced and body jobs, work performed',
      'Repeat-visit vehicles flagged',
      'Warranty items and turnaround times',
    ],
    agenda: ['Service pricing vs market, incl. mobile unit', 'New service manager', 'Service network locations', 'Booking process'],
    deliverables: [
      { tag: 'Menu', what: 'Service & repair menu with flat rates and times — priced against market, mobile unit included' },
      { tag: 'Booking', what: 'Booking process with proactive customer contact — the service visit is scheduled, not awaited' },
      { tag: 'Network', what: 'Service network locations plan and the new service manager appointment' },
      { tag: 'Turnaround', what: 'Repair turnaround reporting — accident cars back on the road is a retention lever' },
    ],
  },
  sp: {
    id: 'sp',
    code: 'SP',
    name: 'Spare Parts',
    stage: 'Stage 4 — Parts',
    lead: 'SP Manager — vacant',
    model: 'Reports to Aftersales Director · dotted to Product Planning',
    mission:
      'Runs parts as a business with a formula, not a guess: stock levels derived from vehicles in operation, service schedules, accident rates and a quality factor for Chinese vehicles.',
    kpis: ['Revenue — direct', 'Cash — collections', 'Volume — ratio-linked'],
    responsibilities: [
      'Demand formula: vehicles in operation × service needs + accident allowance + quality factor',
      'Ordering plan, kept-items list, minimum stock levels',
      'Transport, insurance and storage of parts',
      'Sales target plan for parts; branches + sub-dealers (Silver Age)',
      'Golden Age: parts for other Chery-group brands',
    ],
    reporting: ['Item stock levels vs vehicles on the road', 'Pricing, special-order items, branch network', 'Weekly transactions'],
    agenda: [
      '2026 parts ordering plan',
      'Quick-lube / accident / quality assessment',
      '’23–’26 and ’26+ vehicle parts plans',
      'Branch network review',
    ],
    deliverables: [
      {
        tag: 'Order plan',
        what: '2026 parts ordering plan from the demand formula: vehicles in operation × service needs + accident allowance + quality factor',
      },
      { tag: 'Stock list', what: 'Kept-items list with minimum stock levels — split for ’23–’26 cars vs ’26+ models' },
      { tag: 'Branches', what: 'Branch network review — where parts availability meets the customer' },
    ],
  },
  fa: {
    id: 'fa',
    code: 'FA',
    name: 'Finance & Accounting',
    stage: 'Stage 5 — Finance',
    lead: 'Fadi Ghawi (acting) · CFO M. Abu Dabaseh',
    model: 'Accounting manager under the CFO',
    mission:
      'The control function: consolidates every revenue stream into one P&L, watches the cash in and out, and makes sure volume is achieved at a profit.',
    kpis: ['Cash — control', 'P&L — monitoring'],
    responsibilities: [
      'P&L: vehicle, parts, service and body-shop revenue vs cost of sales',
      'Expense capture: people, marketing, rent, facilities, security',
      'Cash flow: payments out, collections in',
      'Financial control over pricing and margin decisions',
    ],
    reporting: ['Monthly P&L by revenue stream', 'Cash position and flow'],
    agenda: ['3-year expenses forecast', 'Finalize stuck invoicing'],
    deliverables: [
      { tag: 'Forecast', what: '3-year expenses forecast — the cost base the ages are priced against' },
      { tag: 'P&L', what: 'Monthly P&L by revenue stream: vehicles, parts, service, body shop' },
      { tag: 'Invoicing', what: 'Stuck invoicing finalized — cash discipline starts with the backlog' },
    ],
  },
};
